import { apiClient, postForm } from '@/shared/api/client';
import { getListRows } from '@/shared/lib/pagination';
import { toOptions } from '@/shared/lib/options';
import { fetchRegionOptions } from '@/pages/place/api';
import type { PlaceItem } from '@/shared/api/places';
import type { ListParams, ListResponse } from '@/shared/ui/list/types';
import { DASHBOARD_COUNTRY_ID } from './constants';

export interface DashboardParams {
  cityId: string;
  segment: string;
  bidSource: string;
  /** Unix seconds; omitted for "all time". */
  timeFrom: number | '';
  timeTo: number | '';
}

export type DashboardData = Record<string, number | string | null | undefined>;

export async function fetchDashboard(params: DashboardParams) {
  const res = await postForm<{ data?: DashboardData }>('/statistic/dashboard/', {
    country_id: DASHBOARD_COUNTRY_ID,
    city_id: params.cityId,
    segment: params.segment,
    bid_source: params.bidSource,
    time_from: params.timeFrom,
    time_to: params.timeTo,
  });
  if (!res.data) throw new Error('Отсутствуют данные');
  return res.data;
}

export async function fetchStatRegionOptions(countryId: string) {
  return toOptions(await fetchRegionOptions(countryId));
}

/** Cities of a region (first page, as in the legacy filter); nothing until a region is chosen. */
export async function fetchStatCityOptions(regionId: string) {
  if (!regionId) return [];
  const res = await postForm<ListResponse>('/place/city/list/', { page: 1, region_id: regionId });
  return toOptions(getListRows<PlaceItem>(res, ['cities']));
}

export const fetchStatCount = (params: ListParams) =>
  postForm<ListResponse>('/statistic/count/', params);

export const fetchStatServiceList = (params: ListParams) =>
  postForm<ListResponse>('/statistic/service/list/', params);

export const fetchBasicAnalytics = (params: ListParams) =>
  postForm<ListResponse>('/statistic/basic-analytics/list/', params);

interface StoLogsResponse {
  data?: { logs?: unknown[] };
  meta?: { pagination?: unknown };
}

const DEFAULT_STO_SORT = { sort_field: 'create_time', sort_order: 'desc' };

/** "Показы СТО" lives on the site API (GET); reshaped to the `{ logs, pagination }` list contract. */
export async function fetchStatStoLogs(params: ListParams): Promise<ListResponse> {
  const query = Object.fromEntries(
    Object.entries({ ...DEFAULT_STO_SORT, ...params }).filter(([, value]) => value !== '' && value != null),
  );
  const { data } = await apiClient.get<StoLogsResponse>('/v2/map/default/logging-search-services/', {
    baseURL: __CONFIG__.SITE_API_URL,
    params: query,
  });
  return { logs: data.data?.logs ?? [], pagination: data.meta?.pagination };
}
